import { Component } from 'react';
import { connect } from 'react-redux';
import { userInfoSync } from 'hub-http/userInfo';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import H2 from 'UIComponents/elements/headings/H2';
import UIModalPanel from 'UIComponents/dialog/UIModalPanel';
import UIDialogHeader from 'UIComponents/dialog/UIDialogHeader';
import UIDialogBody from 'UIComponents/dialog/UIDialogBody';
import UIDialogFooter from 'UIComponents/dialog/UIDialogFooter';
import UIForm from 'UIComponents/form/UIForm';
import UIDialogCloseButton from 'UIComponents/dialog/UIDialogCloseButton';
import UIFormControl from 'UIComponents/form/UIFormControl';
import UITextInput from 'UIComponents/input/UITextInput';
import UILoadingButton from 'UIComponents/button/UILoadingButton';
import UILink from 'UIComponents/link/UILink';
import UISelect from 'UIComponents/input/UISelect';
import UILoadingSpinner from 'UIComponents/loading/UILoadingSpinner';
import UITooltip from 'UIComponents/tooltip/UITooltip';
import UIAlert from 'UIComponents/alert/UIAlert';
import FloatingAlertStore from 'UIComponents/alert/FloatingAlertStore';
import Routes from 'ContentUtils/Routes';
import { saveEmail as saveEmailAction } from 'ContentData/actions/Emails';
import { fetchEmail } from 'ContentData/api/Emails';
import { getBusinessUnitId } from 'ContentData/selectors/branding';
import FormsContext from 'ContentComponents/context/FormsContext';
import EmailAddressSelect from 'ContentComponents/cmv2/form/formAutomation/followupEmail/emailEditor/EmailAddressSelect';
import FooterSelect from 'ContentComponents/cmv2/form/formAutomation/followupEmail/emailEditor/FooterSelect';
import SubjectField from 'ContentComponents/cmv2/form/formAutomation/followupEmail/emailEditor/SubjectField';
import * as RequestStatuses from 'ContentUtils/constants/RequestStatus';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
var EmailSavingErrorTypes;
(function (EmailSavingErrorTypes) {
  EmailSavingErrorTypes["CONTENT_SCOPE_RESTRICTED"] = "CONTENT_SCOPE_RESTRICTED";
  EmailSavingErrorTypes["REPLY_TO_UNVERIFIED_EMAIL"] = "REPLY_TO_UNVERIFIED_EMAIL";
  EmailSavingErrorTypes["VALIDATION_FAILED"] = "VALIDATION_FAILED";
})(EmailSavingErrorTypes || (EmailSavingErrorTypes = {}));
const KEYS_REQUIRED_TO_CREATE = ['name', 'fromName', 'replyTo', 'subject', 'primaryRichTextModuleHtml', 'subscription', 'canSpamSettingsId'];
function mapStateToProps(state, props) {
  return {
    businessUnitId: getBusinessUnitId(state, props)
  };
}
const mapDispatchToProps = {
  saveEmail: saveEmailAction
};
const connector = connect(mapStateToProps, mapDispatchToProps);
const i18nKey = key => `ContentComponents.cmv2.fields.form.formWorkflowDependencies.followupEmailWorkflows.emailEditorPanel.${key}`;
class EmailEditorPanel extends Component {
  constructor(props) {
    super(props);
    this.updateEmail = this.updateEmail.bind(this);
    this.onEmailSave = this.onEmailSave.bind(this);
    this.state = {
      email: null,
      emailSavingStatus: RequestStatuses.UNINITIALIZED,
      showEmailFetchError: false,
      showUnverifiedEmailError: false
    };
  }
  componentDidMount() {
    const {
      emailId
    } = this.props;
    if (emailId) {
      // Must fetch email even if it's already in state because we need ALL the email data
      // and automated emails fetch only returns a subset of the properties we need
      fetchEmail(emailId).then(emailResponse => {
        this.setState({
          email: emailResponse
        });
      }).catch(() => {
        this.setState({
          showEmailFetchError: true
        });
      });
    }
  }
  updateEmail(prop, newValue) {
    const {
      email
    } = this.state;
    this.setState({
      email: Object.assign({}, email, {
        [prop]: newValue
      })
    });
  }
  onEmailSave() {
    const {
      emailId,
      onClose,
      saveEmail
    } = this.props;
    const {
      email
    } = this.state;
    if (!email) return;
    this.setState({
      emailSavingStatus: RequestStatuses.PENDING
    });
    const emailData = KEYS_REQUIRED_TO_CREATE.reduce((acc, key) => {
      acc[key] = email[key];
      return acc;
    }, {});
    if (!emailId && this.hasBusinessUnitsAccess) {
      emailData.businessUnitId = this.businessUnitId;
    }
    saveEmail(emailData, emailId).then(response => {
      this.setState({
        emailSavingStatus: RequestStatuses.SUCCEEDED
      });
      onClose(emailId || response.id);
    }).catch(error => {
      const {
        errorType,
        errors
      } = error.responseJSON || {};
      if (errorType === EmailSavingErrorTypes.CONTENT_SCOPE_RESTRICTED) {
        this.setState({
          emailSavingStatus: RequestStatuses.FAILED
        });
        FloatingAlertStore.addAlert({
          titleText: /*#__PURE__*/_jsx(FormattedMessage, {
            message: i18nKey('noPublishAccessError.title')
          }),
          message: /*#__PURE__*/_jsx(FormattedMessage, {
            message: i18nKey('noPublishAccessError.message')
          }),
          type: 'danger'
        });
      } else if (errorType === EmailSavingErrorTypes.VALIDATION_FAILED && !!errors.some(err => err.errorType === EmailSavingErrorTypes.REPLY_TO_UNVERIFIED_EMAIL)) {
        this.setState({
          emailSavingStatus: RequestStatuses.FAILED,
          showUnverifiedEmailError: true
        });
      } else {
        this.setState({
          emailSavingStatus: RequestStatuses.FAILED
        });
      }
    });
  }
  isSaveDisabled() {
    const {
      email
    } = this.state;
    return !email || KEYS_REQUIRED_TO_CREATE.some(key => {
      return !email[key];
    });
  }
  get scopes() {
    if (!this._scopes) {
      const {
        user
      } = userInfoSync();
      this._scopes = user.scopes;
    }
    return this._scopes;
  }
  get hasBusinessUnitsAccess() {
    return Routes.isUngated('campaign-business-unit') && this.scopes.includes('business-units-access');
  }
  get businessUnitId() {
    const {
      businessUnitId
    } = this.props;
    const {
      email
    } = this.state;
    return email && Number.isInteger(email.businessUnitId) ? email.businessUnitId : businessUnitId || 0;
  }
  renderSubscriptionTypeSelect() {
    const {
      emailId,
      pickerComponents
    } = this.props;
    const {
      SubscriptionTypeSelect
    } = pickerComponents;
    const {
      email
    } = this.state;
    const {
      subscription
    } = email || {};
    const isEditingTransactionalEmail = emailId && subscription === -1;
    if (isEditingTransactionalEmail || !SubscriptionTypeSelect) {
      return null;
    }
    const customFilter = _subscription => {
      if (this.hasBusinessUnitsAccess) {
        return _subscription.businessUnitId === this.businessUnitId;
      }
      return true;
    };
    if (Routes.isUngated('subscriptions:groupSettings')) {
      return /*#__PURE__*/_jsx(SubscriptionTypeSelect, {
        defaultTypesText: /*#__PURE__*/_jsx(FormattedMessage, {
          message: i18nKey('subscription.defaultTypesText')
        }),
        includeInternal: false,
        isUngatedForSubscriptionGroups: true,
        onChange: type => this.updateEmail('subscription', type ? type.id : null),
        onGroupChange: e => this.updateEmail('preferencesGroupId', e.target.value),
        renderGroupSelect: groupSelectProps => /*#__PURE__*/_jsx(UIFormControl, {
          label: /*#__PURE__*/_jsx(FormattedMessage, {
            message: i18nKey('preferencesGroupId.label')
          }),
          required: true,
          children: /*#__PURE__*/_jsx(UISelect, Object.assign({
            placeholder: /*#__PURE__*/_jsx(FormattedMessage, {
              message: i18nKey('preferencesGroupId.placeholder')
            })
          }, groupSelectProps))
        }),
        renderTypeSelect: typeSelectProps => /*#__PURE__*/_jsx(UIFormControl, {
          label: /*#__PURE__*/_jsx(FormattedMessage, {
            message: i18nKey('subscription.label')
          }),
          required: true,
          children: /*#__PURE__*/_jsx(UISelect, Object.assign({
            placeholder: /*#__PURE__*/_jsx(FormattedMessage, {
              message: i18nKey('subscription.placeholder')
            })
          }, typeSelectProps))
        }),
        value: subscription
      });
    }
    return /*#__PURE__*/_jsx(SubscriptionTypeSelect, {
      customFilter: customFilter,
      customFooter: /*#__PURE__*/_jsx(UILink, {
        href: Routes.settings('email', 'types'),
        external: true,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: i18nKey('subscription.dropdownFooterLink')
        })
      }),
      formControlProps: {
        label: /*#__PURE__*/_jsx(FormattedMessage, {
          message: i18nKey('subscription.label')
        }),
        required: true
      },
      hideInternal: true,
      isUngatedForSubscriptionGroups: false,
      placeholder: /*#__PURE__*/_jsx(FormattedMessage, {
        message: i18nKey('subscription.placeholder')
      }),
      onChange: type => this.updateEmail('subscription', type ? type.id : null),
      value: subscription
    });
  }
  renderEmailEditorLink() {
    const {
      emailId,
      hasAutomatedEmailEditorAccess
    } = this.props;
    if (!hasAutomatedEmailEditorAccess) {
      return null;
    }
    let emailEditUrl;
    if (emailId) {
      emailEditUrl = Routes.editEmail(emailId);
    } else {
      emailEditUrl = Routes.emailContentCreator({
        emailType: 'AUTOMATED',
        businessUnitId: this.hasBusinessUnitsAccess ? this.businessUnitId : null
      });
    }
    return /*#__PURE__*/_jsx("p", {
      children: /*#__PURE__*/_jsx(FormattedJSXMessage, {
        message: i18nKey('helpText_jsx'),
        options: {
          external: true,
          href: emailEditUrl,
          style: {
            whiteSpace: 'nowrap'
          }
        },
        elements: {
          Link: UILink
        }
      })
    });
  }
  renderEmailEditForm() {
    const {
      emailId,
      pickerComponents
    } = this.props;
    const {
      email,
      showUnverifiedEmailError
    } = this.state;
    const {
      canSpamSettingsId,
      name,
      fromName,
      primaryRichTextModuleHtml,
      replyTo,
      subject
    } = email || {};
    return /*#__PURE__*/_jsxs(UIForm, {
      children: [this.renderEmailEditorLink(), /*#__PURE__*/_jsx(UIFormControl, {
        label: /*#__PURE__*/_jsx(FormattedMessage, {
          message: i18nKey('emailName.label')
        }),
        required: true,
        children: /*#__PURE__*/_jsx(UITextInput, {
          onChange: e => this.updateEmail('name', e.target.value),
          value: name || ''
        })
      }), /*#__PURE__*/_jsx(UIFormControl, {
        label: /*#__PURE__*/_jsx(FormattedMessage, {
          message: i18nKey('fromName.label')
        }),
        required: true,
        children: /*#__PURE__*/_jsx(UITextInput, {
          onChange: e => this.updateEmail('fromName', e.target.value),
          value: fromName || ''
        })
      }), /*#__PURE__*/_jsx(EmailAddressSelect, {
        showUnverifiedEmailError: showUnverifiedEmailError,
        onChange: newAddress => this.updateEmail('replyTo', newAddress),
        value: replyTo
      }), this.renderSubscriptionTypeSelect(), /*#__PURE__*/_jsx(SubjectField, {
        pickerComponents: pickerComponents,
        value: subject || '',
        onChange: value => this.updateEmail('subject', value)
      }), /*#__PURE__*/_jsx(UIFormControl, {
        label: /*#__PURE__*/_jsx(FormattedMessage, {
          message: i18nKey('primaryRichTextModuleHtml.label')
        }),
        required: true,
        children: /*#__PURE__*/_jsx(FormsContext.Consumer, {
          children: ({
            RichTextComponent
          }) => RichTextComponent && /*#__PURE__*/_jsx(RichTextComponent, {
            id: emailId ? email && email.id : null // if editing an existing email, this will force a re-render when the email data loads
            ,
            isFormFollowupEmail: true,
            onFieldChange: newEmailBody => this.updateEmail('primaryRichTextModuleHtml', newEmailBody),
            value: primaryRichTextModuleHtml
          })
        })
      }), /*#__PURE__*/_jsx(FooterSelect, {
        onChange: newFooterId => this.updateEmail('canSpamSettingsId', newFooterId),
        value: canSpamSettingsId
      })]
    });
  }

  // TODO: add a delete email button?
  render() {
    const {
      emailId,
      onClose
    } = this.props;
    const {
      email,
      emailSavingStatus,
      showEmailFetchError
    } = this.state;
    const isSaveDisabled = this.isSaveDisabled();
    let content;
    if (showEmailFetchError) {
      content = /*#__PURE__*/_jsx(UIAlert, {
        titleText: /*#__PURE__*/_jsx(FormattedMessage, {
          message: i18nKey('emailFetchError.title')
        }),
        type: "danger",
        className: "m-bottom-6",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: i18nKey('emailFetchError.message')
        })
      });
    } else if (emailId && !email) {
      content = /*#__PURE__*/_jsx(UILoadingSpinner, {
        layout: "centered",
        grow: true
      });
    } else {
      content = this.renderEmailEditForm();
    }
    return /*#__PURE__*/_jsxs(UIModalPanel, {
      width: 460,
      children: [/*#__PURE__*/_jsxs(UIDialogHeader, {
        children: [/*#__PURE__*/_jsx(H2, {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: i18nKey('header')
          })
        }), /*#__PURE__*/_jsx(UIDialogCloseButton, {
          onClick: () => onClose()
        })]
      }), /*#__PURE__*/_jsx(UIDialogBody, {
        children: content
      }), /*#__PURE__*/_jsx(UIDialogFooter, {
        children: /*#__PURE__*/_jsx(UITooltip, {
          placement: "top",
          title: /*#__PURE__*/_jsx(FormattedMessage, {
            message: i18nKey('saveButton.disabledTooltip')
          }),
          disabled: !isSaveDisabled,
          children: /*#__PURE__*/_jsx(UILoadingButton, {
            disabled: isSaveDisabled,
            size: "small",
            use: "tertiary",
            keepResultAfterFinish: true,
            responsive: false,
            onClick: this.onEmailSave,
            loading: emailSavingStatus === RequestStatuses.PENDING,
            failed: emailSavingStatus === RequestStatuses.FAILED,
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: i18nKey('saveButton.label')
            })
          })
        })
      })]
    });
  }
}
export default connector(EmailEditorPanel);